import React from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Target, TrendingUp, ArrowRight, Activity, Sparkles } from 'lucide-react';
import { solutions } from '../../data/mockData';

const iconMap = {
  chart: BarChart3,
  target: Target,
  trending: TrendingUp,
};

const SolutionsSection = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Our Solutions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Comprehensive AI-powered tools designed to transform how CXOs understand and improve organizational performance.
          </p>
        </div>

        {/* Featured: Digital Maturity Assessment */}
        <div className="mb-12">
          <Link to="/digital-maturity" className="block group">
            <div className="relative bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 md:p-10 overflow-hidden hover:shadow-2xl transition-all duration-300">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
              </div>
              
              <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Activity className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl md:text-3xl font-bold text-white">Digital Maturity Assessment</h3>
                      <span className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        NEW
                      </span>
                    </div>
                    <p className="text-gray-300 max-w-xl">
                      Discover your organization&apos;s digital transformation readiness with our AI-powered assessment. Get executive-level insights, industry benchmarks, and a strategic roadmap.
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center gap-2 bg-white text-gray-900 font-semibold px-6 py-3 rounded-lg group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                    Start Assessment
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Solutions Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {solutions.map((solution, index) => {
            const IconComponent = iconMap[solution.icon];
            return (
              <div
                key={solution.id}
                className="group relative bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                {/* Decorative line */}
                <div className="absolute top-0 left-8 right-8 h-1 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                
                {/* Icon */}
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <IconComponent className="w-7 h-7 text-emerald-600" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {solution.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {solution.description}
                </p>

                {/* Link */}
                <Link
                  to={`/${solution.id}`}
                  className="inline-flex items-center text-emerald-600 font-medium hover:text-emerald-700 group/link"
                >
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                </Link>

                {/* Index number */}
                <div className="absolute top-8 right-8 text-6xl font-bold text-gray-100 group-hover:text-emerald-100 transition-colors">
                  {String(index + 1).padStart(2, '0')}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-lg hover:shadow-xl"
          >
            Start Assessment
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;
